"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.webMessengerContextDefaultValue = exports.webMessengerContext = exports.useWebMessenger = exports.WebMessengerProvider = void 0;
var _presentationHooks = require("@ori/presentation-hooks");
var _react = require("react");
var _useGetWebMessengerConfiguration = require("../hooks/useGetWebMessengerConfiguration");
var _utils = require("../utils");
var _purgeLocalStorage = require("../utils/purgeLocalStorage");
var _jsxRuntime = require("react/jsx-runtime");
/* istanbul ignore file @preserve -- Does not make sense to test loading of script */

/* eslint-disable new-cap -- Genesys preset naming */

const webMessengerContextDefaultValue = exports.webMessengerContextDefaultValue = {
  isChatOpened: false,
  isScriptLoaded: false,
  isMessengerReady: false,
  loading: false,
  openChatWindow: () => {},
  closeChatWindow: () => {},
  deploymentId: ''
};
const webMessengerContext = exports.webMessengerContext = /*#__PURE__*/(0, _react.createContext)(webMessengerContextDefaultValue);
const {
  Provider
} = webMessengerContext;
const MESSENGER_DELAY = 3000;
const WebMessengerProvider = ({
  preloadGenesysScript,
  children
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = (0, _react.useState)(false);
  const [isMessengerReady, setIsMessengerReady] = (0, _react.useState)(false);
  const [loading, setLoading] = (0, _react.useState)(false);
  const [isChatOpened, setIsChatOpened] = (0, _react.useState)(false);
  const {
    data
  } = (0, _useGetWebMessengerConfiguration.useGetWebMessengerConfiguration)();
  const {
    deploymentId,
    environment,
    scriptUrl
  } = data ?? {};
  const events = (0, _react.useMemo)(() => [{
    name: 'beforeunload',
    listener: _purgeLocalStorage.purgeLocalStorage
  }], []);
  (0, _presentationHooks.useGlobalEvents)(events);
  const loadScript = (0, _react.useCallback)(() => {
    const onLoad = () => {
      setIsScriptLoaded(true);
      window.Genesys?.('subscribe', 'Messenger.Ready', () => setIsMessengerReady(true));
    };
    const script = (0, _utils.prepareGenesysScript)({
      deploymentId,
      environment,
      scriptUrl
    });
    if (script) {
      script.addEventListener('load', onLoad);
      document.head.append(script);
      return () => script.remove();
    }
    return undefined;
  }, [deploymentId, environment, scriptUrl]);
  (0, _react.useEffect)(() => {
    if (preloadGenesysScript && deploymentId && !isScriptLoaded) {
      loadScript();
    }
  }, [deploymentId, isScriptLoaded, loadScript, preloadGenesysScript]);
  const openChatWindow = (0, _react.useCallback)(() => {
    setIsChatOpened(true);
    if (!isScriptLoaded) {
      setLoading(true);
      loadScript();
    }
    (0, _utils.waitAndExecute)(() => {
      window.Genesys?.('command', 'Messenger.open');
      setLoading(false);
    }, isScriptLoaded && isMessengerReady ? 0 : MESSENGER_DELAY);
  }, [isMessengerReady, loadScript, isScriptLoaded]);
  const closeChatWindow = (0, _react.useCallback)(() => {
    setIsChatOpened(false);
    if (isScriptLoaded && isMessengerReady) {
      window.Genesys?.('command', 'Messenger.close');
    }
  }, [isMessengerReady, isScriptLoaded]);
  (0, _react.useEffect)(() => {
    if (isScriptLoaded && isMessengerReady) {
      window.Genesys?.('subscribe', 'Messenger.closed', closeChatWindow);
    }
  }, [isMessengerReady, closeChatWindow, isScriptLoaded]);
  const value = (0, _react.useMemo)(() => ({
    isScriptLoaded,
    isChatOpened,
    openChatWindow,
    closeChatWindow,
    loading,
    deploymentId,
    isMessengerReady
  }), [isMessengerReady, isScriptLoaded, isChatOpened, openChatWindow, closeChatWindow, loading, deploymentId]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(Provider, {
    value: value,
    children: children
  });
};
exports.WebMessengerProvider = WebMessengerProvider;
const useWebMessenger = () => (0, _react.useContext)(webMessengerContext);
exports.useWebMessenger = useWebMessenger;